import React from 'react';
import styled from 'styled-components';
import { publicImageUrl } from '../../../lib/publicImageUrl';
import { colors, media } from '../../../../shared/lib/styles';
import Tick1 from '../../../../shared/components/Icon/components/Tick1';
import X1 from '../../../../shared/components/Icon/components/X1';
import Image from '../../../../shared/components/Image';
import Scrollable from '../../../../shared/components/Scrollable';
import Text from '../../../../shared/components/Text';
import {
    DataRow,
    DesktopTitle,
    SubHeading,
    TableBody,
    TableCell,
    TableRow,
} from '../../../../shared/components/Table';
import { WebBuilder, Features } from '../../WebBuilderSelector/webBuilders';

// Adapted from StyledColumns in HowItWorksSection
const Container = styled.div`
    width: 100%;
    max-width: 1270px;
    min-width: 910px;
    flex-shrink: 1;
`;

const Table = styled.table`
    // Applied to tr elements directly as grid doesn't nest well
    thead > tr,
    tbody > tr {
        display: grid;
        grid-template-columns: repeat(7, minmax(100px, 130px));
        @media ${media('normal')}, ${media('large')} {
            grid-template-columns: 300px repeat(7, minmax(100px, 1fr));
        }
    }

    // Span heading columns across the table
    th[scope='colgroup'] {
        grid-column: 1 / -1;
    }
`;

function formatValue(value: string | boolean): React.ReactNode {
    if (typeof value === 'boolean') {
        const iconSize = 21;

        return value ? (
            <Tick1 width={iconSize} height={iconSize} aria-label="Yes" />
        ) : (
            <X1 color={colors.grey500} width={iconSize} height={iconSize} aria-label="No" />
        );
    }

    return (
        <Text
            style={{ color: colors.grey700, fontSize: '14px' }}
            baseClass="paragraph"
            variations={['no-margin']}
        >
            {value}
        </Text>
    );
}

const getFeatureTitle = (key: string) =>
    ({
        freeWebHost: __('Free Web Hosting'),
        shareIdeas: __('Share Ideas / Blog'),
        showcasePortfolio: __('Showcase Your Portfolio'),
        basicOnlineStore: __('Basic Online Store'),
        sellUnlimitedProduct: __('Sell Unlimited Products'),
        advancedEcommerceTool: __('Advanced Ecommerce Tools'),
        sellB2B: __('Sell Business to Business (B2B)'),
    }[key] ?? '');

const getPricingStructureValueTitle = (key: string) =>
    ({
        both: __('Monthly or Annual'),
        annually: __('Annual'),
        monthly: __('Monthly'),
    }[key] ?? '');

function ComparisonTable({ webBuilders }: { webBuilders: WebBuilder[] }) {
    // 8 on normal/large, 7 on small/medium
    const numColumns = webBuilders.length;

    return (
        <Scrollable variations={['full-page-width']}>
            <Container>
                <Table>
                    <thead>
                        <TableRow removeBorder>
                            {/* Empty heading to align content */}
                            <DesktopTitle as="th" />
                            {webBuilders.map((builder) => (
                                <TableCell key={builder.id} as="th" scope="col">
                                    <Image
                                        style={{
                                            maxHeight: '25px',
                                        }}
                                        alt={builder.title}
                                        src={publicImageUrl(`website-project/${builder.image}`)}
                                    />
                                </TableCell>
                            ))}
                        </TableRow>
                    </thead>
                    <TableBody>
                        <SubHeading colSpan={numColumns}>{__('Features')}</SubHeading>
                        {[
                            'freeWebHost',
                            'shareIdeas',
                            'showcasePortfolio',
                            'basicOnlineStore',
                            'sellUnlimitedProduct',
                            'advancedEcommerceTool',
                            'sellB2B',
                        ].map((feature) => (
                            <DataRow
                                key={feature}
                                colSpan={numColumns}
                                title={getFeatureTitle(feature)}
                                values={webBuilders.map((builder) =>
                                    formatValue(builder.features[feature as keyof Features])
                                )}
                            />
                        ))}
                    </TableBody>
                    <TableBody>
                        <SubHeading colSpan={numColumns}>{__('Pricing')}</SubHeading>
                        <DataRow
                            colSpan={numColumns}
                            title={__('Free Trial Plan')}
                            values={webBuilders.map((builder) =>
                                formatValue(builder.pricing.freeTrial)
                            )}
                        />
                        <DataRow
                            colSpan={numColumns}
                            title={__('Pricing Structure')}
                            values={webBuilders.map((builder) =>
                                formatValue(
                                    getPricingStructureValueTitle(builder.pricing.pricingStructure)
                                )
                            )}
                        />
                    </TableBody>
                    <TableBody>
                        <SubHeading colSpan={numColumns}>{__('Support')}</SubHeading>
                        <DataRow
                            colSpan={numColumns}
                            title={__('24/7 Support')}
                            values={webBuilders.map((builder) =>
                                formatValue(builder.support.support247)
                            )}
                        />
                        <DataRow
                            colSpan={numColumns}
                            title={__('Live Chat')}
                            values={webBuilders.map((builder) =>
                                formatValue(builder.support.liveChat)
                            )}
                        />
                        <DataRow
                            colSpan={numColumns}
                            title={__('Phone Support')}
                            values={webBuilders.map((builder) =>
                                formatValue(builder.support.phone)
                            )}
                        />
                    </TableBody>
                </Table>
            </Container>
        </Scrollable>
    );
}

export default ComparisonTable;
